/**
 * Kinng.app — Horário de funcionamento do tenant (fonte única no frontend).
 *
 * Lê `brand.salonHours` — o MESMO objeto salvo pelo OnboardingWizard / tela de
 * horários (Account.js HoursSection) e persistido em `tenant.brand.salonHours`
 * no backend. Formato: { mon: { open: bool, from: "HH:MM", to: "HH:MM" }, ... }
 * com chaves mon..sun.
 *
 * O backend tem a lógica equivalente em `deps.get_day_hours` /
 * `deps.format_business_hours`; os fallbacks aqui espelham os de lá:
 *   - sem config, ou dia ausente na config  → ABERTO (08:00–20:00)
 *   - só fecha quando o dia está explicitamente `open: false`
 */

// brand.salonHours é indexado por mon..sun; Date.getDay() é 0=Dom..6=Sáb.
export const DOW_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

export const DEFAULT_DAY_HOURS = { open: true, from: "08:00", to: "20:00" };

/** Config bruta do dia da semana de `date` (ou null se não houver config). */
export const getDayConfig = (salonHours, date) => {
    if (!salonHours || typeof salonHours !== "object") return null;
    return salonHours[DOW_KEYS[date.getDay()]] || null;
};

/** { open, from, to } para `date`, aplicando o fallback aberto 08:00–20:00. */
export const getDayHours = (salonHours, date) => {
    const day = getDayConfig(salonHours, date);
    return day ? { ...DEFAULT_DAY_HOURS, ...day } : { ...DEFAULT_DAY_HOURS };
};

/** true se o salão atende nesse dia (só false quando o dia é `open: false`). */
export const isDayOpen = (salonHours, date) => {
    const day = getDayConfig(salonHours, date);
    if (!day) return true;
    return day.open !== false;
};

/**
 * Próximo dia (delta > 0) / dia anterior (delta < 0) a partir de `fromDate`
 * que tenha expediente. `maxIter` (padrão 7) evita loop infinito no caso
 * extremo de um tenant sem NENHUM dia aberto — nesse caso devolve o primeiro
 * passo na direção pedida, sem travar.
 */
export const nextOpenDay = (salonHours, fromDate, delta = 1, maxIter = 7) => {
    const step = delta >= 0 ? 1 : -1;
    const d = new Date(fromDate);
    for (let i = 0; i < maxIter; i++) {
        d.setDate(d.getDate() + step);
        if (isDayOpen(salonHours, d)) return d;
    }
    const fallback = new Date(fromDate);
    fallback.setDate(fallback.getDate() + step);
    return fallback;
};

/**
 * Se `date` já cai num dia com expediente, devolve a própria `date`;
 * senão avança para o próximo dia aberto (respeitando `maxIter`).
 */
export const ensureOpenDay = (salonHours, date, maxIter = 7) => {
    if (isDayOpen(salonHours, date)) return new Date(date);
    return nextOpenDay(salonHours, date, 1, maxIter);
};
